import React, { useMemo } from 'react';
import {
  TokenCard,
  TokenCardProvider,
  type Token,
  type TokenCardDependencies,
} from '@vaiu/cash-token-card';
import { makeMmMoneyCurrency } from '@vaiu/utils/mmMoney';

/** Stable fake issuer row for previews (wire key suffix only; arbitrary id string). */
const DEFAULT_PREVIEW_ISSUER_ID = 'cafebabecafebabecafebab1';

const PreviewSlot = (): null => null;

function buildStubDeps(issuerUserId: string): TokenCardDependencies {
  return {
    toast: () => {},
    user: { _id: issuerUserId, id: issuerUserId, username: 'you' },
    config: {},
    boPaymentsApi: { getPrepaidCardForToken: async () => null },
    updateTokenConditions: async () => ({}),
    PrepaidCard: PreviewSlot,
    NFCPayment: PreviewSlot,
    InvoicePaymentDecision: PreviewSlot,
  };
}

export interface MmMoneyBrandingSampleTokenCardProps {
  /** Issuer id for realistic `MM$…` preview; defaults when empty. */
  issuerUserId?: string;
  /** Current background artwork URL shown at 50% opacity on TokenCard background layer */
  backgroundUrl: string;
  currencyIconUrl?: string;
}

/**
 * Minimal `CashTokenCardProvider`-free embedding of production **TokenCard** for MM$ styling QA.
 */
export function MmMoneyBrandingSampleTokenCard({
  issuerUserId,
  backgroundUrl,
  currencyIconUrl,
}: MmMoneyBrandingSampleTokenCardProps) {
  const issuer = (issuerUserId || '').trim() || DEFAULT_PREVIEW_ISSUER_ID;
  const deps = useMemo(() => buildStubDeps(issuer), [issuer]);

  const token = useMemo<Token>(
    () => ({
      key: 'SAMPLE-MM-TOKEN',
      amount: 25,
      currency: makeMmMoneyCurrency(issuer),
      status: 'active',
      expiry: new Date(Date.now() + 7 * 864e5).toISOString(),
      type: 'cash',
      mmMoneyBackgroundUrl: backgroundUrl.trim(),
      ...(currencyIconUrl?.trim()
        ? { mmMoneyCurrencyIconUrl: currencyIconUrl.trim(), mmMoneyIssuerUsername: 'you' }
        : {}),
      mmMoneyIssuerDisplayName: 'Preview',
      createdByCurrentUser: true,
    }),
    [issuer, backgroundUrl, currencyIconUrl]
  );

  return (
    <TokenCardProvider value={deps}>
      <TokenCard
        theme="minimal"
        showActions={false}
        showQRCode={false}
        qrMode="button"
        showPrepaidCardOption={false}
        className="max-w-xl"
        mmMoneyBackgroundImageOpacity={0.5}
        token={token}
      />
    </TokenCardProvider>
  );
}
